/**
 * Iter-158.13.2.20f.42-relational — Unassigned AI Breakdown list.
 *
 * Small read-only collapsible presentation for `hm_breakdown_entries`
 * that are related to only ONE side of the (scene, character) pair.
 * Two modes:
 *
 *   mode="scene"      — findings scoped by scene_id whose
 *                       character_ids array is empty. Rendered under
 *                       "Scene-wide AI Hair & Makeup Breakdown".
 *
 *   mode="character"  — findings scoped by character_id whose
 *                       scene_ids array is empty. Rendered under
 *                       "Character-wide AI Hair & Makeup Breakdown".
 *
 * Read-only by design. Mounting an unfiltered DeptBreakdownSection
 * with a single-sided scope would ALSO return the paired entries
 * and duplicate what each Character × Scene accordion already shows
 * — so we reuse the same records via a client-side filter and do
 * not add a second CRUD implementation.
 *
 *   - default collapsed
 *   - non-empty guard: the whole section is omitted when there are
 *     no genuinely unassigned findings, so the surface stays quiet.
 *
 * Props:
 *   apiBase      workspace-context API base (from useApiBase())
 *   projectId    canonical project id
 *   mode         "scene" | "character"
 *   targetId     the scene id (mode="scene") or character id
 *                (mode="character")
 *   testidPrefix testid namespace (e.g. "scene-wide-ai" or
 *                "character-wide-ai")
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";


export default function UnassignedAIBreakdownList({
    apiBase,
    projectId,
    mode,
    targetId,
    testidPrefix,
}) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [findings, setFindings] = useState([]);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!apiBase || !projectId || !targetId) return;
        let alive = true;
        const params =
            mode === "scene"
                ? { scene_id: targetId }
                : { character_id: targetId };
        setLoading(true);
        api
            .get(`${apiBase}/${projectId}/hm-breakdown-entries`, { params })
            .then(({ data }) => {
                if (!alive) return;
                setFindings(Array.isArray(data) ? data : []);
                setError("");
            })
            .catch((e) => {
                if (alive) setError(formatApiError(e));
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [apiBase, projectId, mode, targetId]);

    // Genuinely unassigned = the OTHER side of the pair is empty.
    // Character-scoped entries surface inside each per-pair
    // accordion; Scene-scoped entries surface inside each per-pair
    // accordion on the other page. This section carries ONLY the
    // remainder so the same document is never presented twice.
    const unassigned = useMemo(() => {
        return findings.filter((f) => {
            const other =
                mode === "scene"
                    ? Array.isArray(f.character_ids) ? f.character_ids : []
                    : Array.isArray(f.scene_ids) ? f.scene_ids : [];
            return other.length === 0;
        });
    }, [findings, mode]);

    // Keep the surface quiet when there is nothing genuinely
    // unassigned — matches the "only appear if legitimate" rule.
    if (loading) return null;
    if (error) {
        return (
            <p
                data-testid={`${testidPrefix}-error-${targetId}`}
                className="mt-3 text-[11px] text-red-300"
            >
                {error}
            </p>
        );
    }
    if (unassigned.length === 0) return null;

    const label =
        mode === "scene"
            ? "Scene-wide AI Hair & Makeup Breakdown"
            : "Character-wide AI Hair & Makeup Breakdown";

    return (
        <div
            data-testid={`${testidPrefix}-wrap-${targetId}`}
            className="mt-4 rounded-xl border border-white/10 bg-card"
        >
            <button
                type="button"
                data-testid={`${testidPrefix}-toggle-${targetId}`}
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
                className="tap w-full flex items-center justify-between gap-2 px-4 py-3 text-left text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-semibold hover:bg-white/[0.03]"
                title={open ? "Collapse" : "Expand"}
            >
                <span>{label}</span>
                {open ? (
                    <ChevronDown size={12} className="text-neutral-400" />
                ) : (
                    <ChevronRight size={12} className="text-neutral-400" />
                )}
            </button>
            {open ? (
                <ul
                    data-testid={`${testidPrefix}-body-${targetId}`}
                    className="px-4 pb-4 pt-1 space-y-2"
                >
                    {unassigned.map((f) => (
                        <li
                            key={f.id}
                            data-testid={`${testidPrefix}-entry-${targetId}-${f.id}`}
                            className="rounded-md border border-white/5 bg-black/20 p-2 text-[12px] text-neutral-200"
                        >
                            {f.title ? (
                                <div className="text-[11px] text-white font-semibold">
                                    {f.title}
                                </div>
                            ) : null}
                            <div className="whitespace-pre-wrap">{f.body}</div>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}
